import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  faDownload,
  faPlus,
  faTrash,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { App, Button, Form, Modal, Spin, Steps, Table, Tag } from 'antd';
import type { UploadFile } from 'antd';
import { styled } from 'styled-components';
import { useCustomers } from '@/api/Customers';
import { useLocations } from '@/api/Locations';
import {
  refreshShippingManifests,
  useAddShippingManifestDocument,
  useAddShippingManifestReference,
  useCreateShippingManifest,
  useDeleteShippingManifestDocument,
  useDeleteShippingManifestReference,
  useUpdateShippingManifest,
  useUpdateShippingManifestReference,
  useVesselPositions,
  usePortCalls,
} from '@/api/Vessel';
import { useTranslate } from '@/translations';
import { Icon } from '@/ui/components';
import { ShippingManifestDetail } from '@/types';
import { DragUpload } from '@/ui';
import {
  formatManifestWhen,
  isManifestDraftReady,
  ManifestDraft,
  manifestProgress,
} from './manifestEditor';
import {
  DraftReference,
  emptyReference,
  ManifestFormValues,
  ManifestOverviewFields,
  ManifestReferenceDialogs,
  ManifestReferenceTable,
  MAX_FILE_BYTES,
  useManifestReferenceEditor,
} from './manifestForm';

const manifestStepItems = (
  t: (key: 'overview' | 'shippingReferences' | 'documents') => string,
) => [
  { title: t('overview') },
  { title: t('shippingReferences') },
  { title: t('documents') },
];

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 18px 0 8px;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 14px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

type AddManifestModalProps = {
  open: boolean;
  onClose: () => void;
};

export const AddManifestModal = ({ open, onClose }: AddManifestModalProps) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [step, setStep] = useState(0);
  const [form] = Form.useForm<ManifestFormValues>();
  const watched = Form.useWatch([], form);
  const [references, setReferences] = useState<DraftReference[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const editor = useManifestReferenceEditor(setReferences);
  const createManifest = useCreateShippingManifest();
  const addReference = useAddShippingManifestReference();
  const addDocument = useAddShippingManifestDocument();
  const vessels = useVesselPositions();
  const customers = useCustomers();
  const locations = useLocations({ enabled: open });
  const portCalls = usePortCalls(undefined, open);
  const positions = vessels.data?.entities ?? [];
  const selectedVessel = positions.find(
    (vessel) => String(vessel.vesselId ?? '') === String(watched?.vesselId),
  );
  const draft: ManifestDraft = {
    manifestRef: watched?.manifestRef ?? '',
    eta: watched?.eta ? watched.eta.toISOString() : undefined,
    vesselId: selectedVessel?.vesselId,
    vesselName: selectedVessel?.shipName,
    vesselJourneyId: watched?.vesselJourneyId,
    movementId: watched?.movementId,
    customerId: watched?.customerId,
    siteId: watched?.siteId,
    notes: watched?.notes,
  };
  const ready = isManifestDraftReady(draft);
  const vehiclesReady =
    references.length > 0 && references.every((item) => item.vins.length > 0);
  const hasVehicles = references.some((item) => item.vins.length > 0);
  const saving =
    createManifest.isPending || addReference.isPending || addDocument.isPending;

  const requireVehicles = () => {
    if (vehiclesReady) return true;
    message.warning(t('vehicleRangeRequired'));
    setStep(1);
    return false;
  };

  const create = async () => {
    let values: ManifestFormValues;
    try {
      values = await form.validateFields();
    } catch {
      setStep(0);
      return;
    }
    const selected = positions.find(
      (vessel) => String(vessel.vesselId ?? '') === String(values.vesselId),
    );
    const next: ManifestDraft = {
      manifestRef: values.manifestRef ?? '',
      eta: values.eta ? values.eta.toISOString() : undefined,
      vesselId: selected?.vesselId,
      vesselName: selected?.shipName,
      vesselJourneyId: values.vesselJourneyId,
      movementId: values.movementId,
      customerId: values.customerId,
      siteId: values.siteId,
      notes: values.notes,
    };
    if (!isManifestDraftReady(next) || !next.eta || !requireVehicles()) return;
    let started = false;
    try {
      started = true;
      const result = await createManifest.mutateAsync({
        manifestRef: next.manifestRef.trim(),
        eta: next.eta,
        vesselId: next.vesselId,
        vesselJourneyId: next.vesselJourneyId,
        vesselName: next.vesselName,
        movementId: next.movementId,
        customerId: next.customerId ?? 0,
        siteId: next.siteId ?? 0,
        notes: next.notes?.trim(),
      });
      if (!result.success || !result.data) {
        message.error(t('generalError'));
        return;
      }
      const id = result.data.id;
      for (const reference of references) {
        const saved = await addReference.mutateAsync({
          id,
          body: {
            referenceType: reference.referenceType,
            reference: reference.reference,
            vehicleCount: reference.vins.length,
            startVin: reference.vins[0],
            vins: reference.vins,
          },
        });
        if (!saved.success) {
          message.error(t('generalError'));
          onClose();
          return;
        }
      }
      for (const file of files) {
        const data = new FormData();
        data.append('file', file);
        data.append('documentType', 'Attachment');
        const uploaded = await addDocument.mutateAsync({ id, file: data });
        if (!uploaded.success) {
          message.error(t('generalError'));
          onClose();
          return;
        }
      }
      message.success(t('createManifest'));
      onClose();
    } finally {
      if (started) refreshShippingManifests();
    }
  };

  return (
    <>
      <Modal
        title={t('addManifest')}
        open={open}
        width={920}
        onCancel={onClose}
        destroyOnHidden
        footer={[
          <Button key="cancel" onClick={onClose}>
            {t('cancel')}
          </Button>,
          step > 0 ? (
            <Button
              key="back"
              onClick={() => setStep((current) => current - 1)}
            >
              {t('back')}
            </Button>
          ) : null,
          hasVehicles ? (
            <Button key="vehicles" onClick={() => editor.setVehiclesOpen(true)}>
              {t('viewVehicles')}
            </Button>
          ) : null,
          step < 2 ? (
            <Button
              key="next"
              type="primary"
              onClick={() => {
                if (step === 1 && !vehiclesReady) {
                  requireVehicles();
                  return;
                }
                setStep((current) => current + 1);
              }}
            >
              {t('nextStep')}
            </Button>
          ) : (
            <Button
              key="create"
              type="primary"
              disabled={!ready}
              loading={saving}
              onClick={() => void create()}
            >
              {t('createManifest')}
            </Button>
          ),
        ]}
      >
        <Steps
          size="small"
          current={step}
          className="mb-2"
          onChange={(next) => {
            if (next > 1 && !vehiclesReady) {
              requireVehicles();
              return;
            }
            setStep(next);
          }}
          items={manifestStepItems(t)}
        />
        <Form form={form} layout="vertical" component="div">
          <div hidden={step !== 0}>
            <ManifestOverviewFields
              form={form}
              variant="create"
              positions={positions}
              customers={customers.data ?? []}
              locations={locations.data ?? []}
              portCalls={portCalls.data ?? []}
              vesselsLoading={vessels.isPending || vessels.isFetching}
              portCallsLoading={portCalls.isPending || portCalls.isFetching}
              customersLoading={customers.isPending || customers.isFetching}
              locationsLoading={locations.isPending || locations.isFetching}
            />
          </div>
          {step === 1 ? (
            <>
              <div style={{ margin: '16px 0 8px', textAlign: 'right' }}>
                <Button onClick={() => editor.openReference(emptyReference())}>
                  {t('addReference')}
                </Button>
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={() => editor.openImport(true)}
                >
                  {t('importReferences')}
                </Button>
              </div>
              <ManifestReferenceTable
                references={references}
                onPatch={editor.patchReference}
                onEdit={(record) => editor.openReference({ ...record })}
                onDelete={editor.removeReference}
                confirmDelete
              />
            </>
          ) : null}
          {step === 2 ? (
            <div style={{ marginTop: 16 }}>
              <DragUpload
                multiple
                maxFileSize={MAX_FILE_BYTES}
                invalidMaxFileSizeMessage={t('fileIsTooLarge')}
                showUploadBtn={false}
                onFileChanged={(list) => {
                  setFiles(
                    list.flatMap((item) =>
                      item.originFileObj ? [item.originFileObj] : [],
                    ),
                  );
                }}
              />
            </div>
          ) : null}
        </Form>
      </Modal>
      <ManifestReferenceDialogs references={references} editor={editor} />
    </>
  );
};

type DraftDocument = {
  key: string;
  id?: number;
  fileName: string;
  documentType?: string | null;
  uploadedBy?: string;
  uploadedAt?: string;
  file?: File;
};

const referenceDrafts = (detail: ShippingManifestDetail): DraftReference[] =>
  detail.shippingReferences.map((reference) => ({
    key: `id-${reference.id}`,
    id: reference.id,
    referenceType: reference.referenceType,
    reference: reference.reference,
    vehicleCount: reference.vehicleCount,
    startVin: reference.startVin || '',
    vins: reference.vins ?? [],
  }));

const documentDrafts = (detail: ShippingManifestDetail): DraftDocument[] =>
  detail.documents.map((document) => ({
    key: `id-${document.id}`,
    id: document.id,
    fileName: document.fileName,
    documentType: document.documentType,
    uploadedBy: document.uploadedBy,
    uploadedAt: document.uploadedAt,
  }));

type EditManifestModalProps = {
  open: boolean;
  loading?: boolean;
  detail?: ShippingManifestDetail;
  onClose: () => void;
  onDownload: (documentId: number, fileName: string) => Promise<void>;
};

export const EditManifestModal = ({
  open,
  loading = false,
  detail,
  onClose,
  onDownload,
}: EditManifestModalProps) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [step, setStep] = useState(0);
  const [headerForm] = Form.useForm<ManifestFormValues>();
  const [referencesDraft, setReferencesDraft] = useState<DraftReference[]>([]);
  const [baselineReferences, setBaselineReferences] = useState<
    DraftReference[]
  >([]);
  const [documentsDraft, setDocumentsDraft] = useState<DraftDocument[]>([]);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<number[]>([]);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<number>();
  const editor = useManifestReferenceEditor(setReferencesDraft);
  const detailRef = useRef(detail);
  const queuedDocumentUids = useRef(new Set<string>());
  detailRef.current = detail;
  const vessels = useVesselPositions();
  const portCalls = usePortCalls(undefined, open);
  const customers = useCustomers();
  const locations = useLocations({ enabled: Boolean(detail) });
  const positions = vessels.data?.entities ?? [];
  const updateManifest = useUpdateShippingManifest();
  const addReference = useAddShippingManifestReference();
  const updateReference = useUpdateShippingManifestReference();
  const deleteReference = useDeleteShippingManifestReference();
  const addDocument = useAddShippingManifestDocument();
  const deleteDocument = useDeleteShippingManifestDocument();

  useEffect(() => {
    const current = detailRef.current;
    if (!current) return;
    headerForm.setFieldsValue({
      manifestRef: current.manifestRef || '',
      eta: current.eta ? dayjs(current.eta) : undefined,
      vesselId: current.vesselId == null ? undefined : String(current.vesselId),
      vesselJourneyId: current.vesselJourneyId,
      customerId: current.customerId,
      movementId: current.movementId,
      siteId: current.siteId,
      notes: current.notes || '',
    });
    const nextReferences = referenceDrafts(current);
    setReferencesDraft(nextReferences);
    setBaselineReferences(nextReferences);
    setDocumentsDraft(documentDrafts(current));
    setRemovedDocumentIds([]);
    setStep(0);
    editor.closeImport();
    // Reset when a different manifest is opened. The editor helpers are stable
    // enough for this load, and including them would reapply the detail.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id, headerForm]);

  const typeLabel = (value?: string | null) => {
    if (value === 'BillOfLading') return t('billOfLading');
    if (value === 'BookingNo') return t('bookingNo');
    if (value === 'CustomerRef') return t('customerRef');
    if (value === 'Manifest') return t('manifestDocument');
    if (value === 'VinList') return t('vinList');
    return value || '—';
  };

  const failed = (success: boolean) => {
    if (!success) message.error(t('generalError'));
    return success;
  };

  const referenceBody = (reference: DraftReference) => ({
    referenceType: reference.referenceType,
    reference: reference.reference.trim(),
    vehicleCount: reference.vins.length,
    startVin: reference.vins[0],
    vins: reference.vins,
  });

  const vehiclesReady = referencesDraft.every((item) => item.vins.length > 0);
  const hasVehicles = referencesDraft.some((item) => item.vins.length > 0);

  const requireVehicles = () => {
    if (vehiclesReady) return true;
    message.warning(t('vehicleRangeRequired'));
    setStep(1);
    return false;
  };

  const save = async () => {
    if (!detail || !requireVehicles()) return;
    let values: ManifestFormValues;
    try {
      values = await headerForm.validateFields();
    } catch {
      setStep(0);
      return;
    }
    const selected = positions.find(
      (vessel) => String(vessel.vesselId ?? '') === String(values.vesselId),
    );
    const parsedVesselId = Number(values.vesselId);
    let started = false;
    try {
      started = true;
      const headerResult = await updateManifest.mutateAsync({
        id: detail.id,
        body: {
          manifestRef: values.manifestRef?.trim(),
          eta: values.eta ? values.eta.toISOString() : undefined,
          vesselId:
            selected?.vesselId ??
            (Number.isFinite(parsedVesselId) ? parsedVesselId : undefined),
          vesselJourneyId: values.vesselJourneyId,
          vesselName: selected?.shipName ?? detail.vesselName,
          movementId: values.movementId,
          customerId: values.customerId ?? 0,
          siteId: values.siteId ?? 0,
          notes: values.notes,
        },
      });
      if (!failed(headerResult.success)) return;

      const keptIds = new Set(
        referencesDraft.flatMap((reference) =>
          reference.id ? [reference.id] : [],
        ),
      );
      for (const reference of baselineReferences) {
        if (!reference.id || keptIds.has(reference.id)) continue;
        const deleted = await deleteReference.mutateAsync({
          id: detail.id,
          referenceId: reference.id,
        });
        if (!failed(deleted.success)) return;
      }

      const savedReferences: DraftReference[] = [];
      for (const reference of referencesDraft) {
        const body = referenceBody(reference);
        const original = baselineReferences.find(
          (item) => item.id === reference.id,
        );
        if (reference.id && original) {
          const unchanged =
            original.referenceType === body.referenceType &&
            original.reference === body.reference &&
            original.vins.join('|') === body.vins.join('|');
          if (!unchanged) {
            const updated = await updateReference.mutateAsync({
              id: detail.id,
              referenceId: reference.id,
              body,
            });
            if (!failed(updated.success)) return;
          }
          savedReferences.push({ ...reference, ...body });
          continue;
        }
        const added = await addReference.mutateAsync({ id: detail.id, body });
        if (!failed(added.success) || !added.data) return;
        savedReferences.push({
          ...reference,
          ...body,
          id: added.data.id,
          key: `id-${added.data.id}`,
        });
      }

      for (const documentId of removedDocumentIds) {
        const deleted = await deleteDocument.mutateAsync({
          id: detail.id,
          documentId,
        });
        if (!failed(deleted.success)) return;
      }

      const savedDocuments: DraftDocument[] = documentsDraft.filter(
        (document) => !document.file,
      );
      for (const document of documentsDraft) {
        if (!document.file) continue;
        const data = new FormData();
        data.append('file', document.file);
        data.append('documentType', 'Attachment');
        const uploaded = await addDocument.mutateAsync({
          id: detail.id,
          file: data,
        });
        if (!failed(uploaded.success) || !uploaded.data) return;
        savedDocuments.push({
          key: `id-${uploaded.data.id}`,
          id: uploaded.data.id,
          fileName: uploaded.data.fileName || document.fileName,
          documentType: uploaded.data.documentType,
          uploadedBy: uploaded.data.uploadedBy,
          uploadedAt: uploaded.data.uploadedAt,
        });
      }

      setReferencesDraft(savedReferences);
      setBaselineReferences(savedReferences);
      setDocumentsDraft(savedDocuments);
      setRemovedDocumentIds([]);
      message.success(t('saveChanges'));
      onClose();
    } finally {
      if (started) refreshShippingManifests();
    }
  };

  const queueDocument = (file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      message.error(t('fileIsTooLarge'));
      return;
    }
    setDocumentsDraft((current) => [
      ...current,
      {
        key: `file-${file.name}-${file.lastModified}`,
        fileName: file.name,
        documentType: 'Attachment',
        file,
      },
    ]);
  };

  const addDroppedDocuments = (list: UploadFile[]) => {
    list.forEach((item) => {
      const file = item.originFileObj;
      if (!file || queuedDocumentUids.current.has(item.uid)) return;
      queuedDocumentUids.current.add(item.uid);
      queueDocument(file);
    });
  };

  const removeDocument = (document: DraftDocument) => {
    Modal.confirm({
      title: t('delete'),
      content: document.fileName,
      onOk: () => {
        setDocumentsDraft((current) =>
          current.filter((item) => item.key !== document.key),
        );
        if (document.id) {
          setRemovedDocumentIds((current) => [
            ...current,
            document.id as number,
          ]);
        }
      },
    });
  };

  const status = manifestProgress(detail?.eta);

  return (
    <>
      <Modal
        title={
          <TitleRow>
            <span>{t('editManifest')}</span>
            {detail ? (
              <Tag color={status === 'inProgress' ? 'success' : 'processing'}>
                {t(status)}
              </Tag>
            ) : null}
          </TitleRow>
        }
        open={open}
        width={980}
        onCancel={onClose}
        footer={[
          <Button key="cancel" onClick={onClose}>
            {t('cancel')}
          </Button>,
          hasVehicles ? (
            <Button key="vehicles" onClick={() => editor.setVehiclesOpen(true)}>
              {t('viewVehicles')}
            </Button>
          ) : null,
          <Button
            key="save"
            type="primary"
            disabled={!detail}
            loading={
              updateManifest.isPending ||
              addReference.isPending ||
              updateReference.isPending ||
              deleteReference.isPending ||
              addDocument.isPending ||
              deleteDocument.isPending
            }
            onClick={() => void save()}
          >
            {t('saveChanges')}
          </Button>,
        ]}
      >
        {detail ? (
          <>
            <Steps
              size="small"
              current={step}
              onChange={(next) => {
                if (next > 1 && !vehiclesReady) {
                  requireVehicles();
                  return;
                }
                setStep(next);
              }}
              items={manifestStepItems(t)}
            />
            <Form
              form={headerForm}
              layout="vertical"
              component="div"
              style={{ marginTop: 16 }}
            >
              <div hidden={step !== 0}>
                <ManifestOverviewFields
                  form={headerForm}
                  variant="edit"
                  positions={positions}
                  customers={customers.data ?? []}
                  locations={locations.data ?? []}
                  portCalls={portCalls.data ?? []}
                  vesselsLoading={vessels.isPending || vessels.isFetching}
                  portCallsLoading={portCalls.isPending || portCalls.isFetching}
                  customersLoading={customers.isPending || customers.isFetching}
                  locationsLoading={
                    Boolean(detail) &&
                    (locations.isPending || locations.isFetching)
                  }
                />
              </div>
              {step === 1 ? (
                <>
                  <SectionHeader>
                    <SectionTitle>
                      {t('shippingReferences')} ({referencesDraft.length})
                    </SectionTitle>
                    <div>
                      <strong style={{ marginRight: 12 }}>
                        {t('totalVehicles')}{' '}
                        {referencesDraft
                          .reduce(
                            (sum, reference) => sum + reference.vins.length,
                            0,
                          )
                          .toLocaleString()}
                      </strong>
                      <Button
                        icon={<Icon icon={faPlus} />}
                        onClick={() => editor.openReference(emptyReference())}
                      >
                        {t('addReference')}
                      </Button>
                      <Button
                        style={{ marginLeft: 8 }}
                        icon={<Icon icon={faUpload} />}
                        onClick={() => editor.openImport(true)}
                      >
                        {t('importReferences')}
                      </Button>
                    </div>
                  </SectionHeader>
                  <ManifestReferenceTable
                    references={referencesDraft}
                    onPatch={editor.patchReference}
                    onEdit={(record) => editor.openReference({ ...record })}
                    onDelete={editor.removeReference}
                    confirmDelete
                  />
                </>
              ) : null}
              {step === 2 ? (
                <>
                  <SectionHeader>
                    <SectionTitle>
                      {t('attachedDocuments')} ({documentsDraft.length})
                    </SectionTitle>
                  </SectionHeader>
                  <DragUpload
                    multiple
                    maxFileSize={MAX_FILE_BYTES}
                    invalidMaxFileSizeMessage={t('fileIsTooLarge')}
                    showUploadBtn={false}
                    showUploadList={false}
                    onFileChanged={addDroppedDocuments}
                  />
                  <Table
                    size="small"
                    rowKey="key"
                    pagination={false}
                    dataSource={documentsDraft}
                    columns={[
                      { title: t('documents'), dataIndex: 'fileName' },
                      {
                        title: t('documentType'),
                        dataIndex: 'documentType',
                        render: (value?: string) => typeLabel(value),
                      },
                      { title: t('uploadedBy'), dataIndex: 'uploadedBy' },
                      {
                        title: t('date'),
                        dataIndex: 'uploadedAt',
                        render: (value: string) => formatManifestWhen(value),
                      },
                      {
                        title: t('actions'),
                        width: 90,
                        render: (_: unknown, document: DraftDocument) => (
                          <>
                            {document.id ? (
                              <Button
                                type="text"
                                aria-label={t('download')}
                                icon={<Icon icon={faDownload} />}
                                loading={downloadingDocumentId === document.id}
                                onClick={() => {
                                  const documentId = document.id as number;
                                  setDownloadingDocumentId(documentId);
                                  void onDownload(
                                    documentId,
                                    document.fileName,
                                  ).finally(() =>
                                    setDownloadingDocumentId(undefined),
                                  );
                                }}
                              />
                            ) : null}
                            <Button
                              type="text"
                              danger
                              aria-label={t('delete')}
                              icon={<Icon icon={faTrash} />}
                              onClick={() => removeDocument(document)}
                            />
                          </>
                        ),
                      },
                    ]}
                  />
                </>
              ) : null}
            </Form>
          </>
        ) : (
          <div style={{ padding: 48, textAlign: 'center' }}>
            {loading ? <Spin /> : t('generalError')}
          </div>
        )}
      </Modal>
      <ManifestReferenceDialogs references={referencesDraft} editor={editor} />
    </>
  );
};
